import {
  type CSSProperties,
  defineComponent,
  h,
  inject,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
} from 'vue';
import type { DefineComponent, PropType } from 'vue';

import {
  Virtualizer as VirtualizerClass,
  type VirtualizerConfig,
} from '../components/Virtualizer';
import { VirtualizerInjectionKey } from './injection';

export interface VirtualizerPropsVue {
  config?: Partial<VirtualizerConfig>;
  contentClass?: string;
  contentStyle?: CSSProperties;
}

// Wraps its default slot in a single scroll container and shares one
// `Virtualizer` instance with descendant File/FileDiff components (which then
// instantiate their `Virtualized*` variants). `class`/`style` fall through to
// the outer scroll container; `contentClass`/`contentStyle` target the inner
// content wrapper. Mirrors the React `Virtualizer` component.
const VirtualizerImpl = defineComponent({
  name: 'PierreVirtualizer',
  props: {
    config: {
      type: Object as PropType<Partial<VirtualizerConfig>>,
      default: undefined,
    },
    contentClass: { type: String, default: undefined },
    contentStyle: {
      type: Object as PropType<CSSProperties>,
      default: undefined,
    },
  },
  setup(props, { slots }) {
    const instance =
      typeof window !== 'undefined'
        ? new VirtualizerClass(props.config)
        : undefined;
    provide(VirtualizerInjectionKey, instance);

    const rootRef = ref<HTMLElement | null>(null);
    onMounted(() => {
      if (rootRef.value != null) {
        instance?.setup(rootRef.value);
      }
    });
    onBeforeUnmount(() => {
      instance?.cleanUp();
    });

    return () =>
      h('div', { ref: rootRef }, [
        h(
          'div',
          { class: props.contentClass, style: props.contentStyle },
          slots.default?.()
        ),
      ]);
  },
});

export const Virtualizer =
  VirtualizerImpl as unknown as DefineComponent<VirtualizerPropsVue>;

// Inject the `Virtualizer` instance provided by a `Virtualizer` ancestor, if
// any. Returns undefined when rendering outside a virtualized scroll container.
export function useVirtualizer(): VirtualizerClass | undefined {
  return inject(VirtualizerInjectionKey, undefined);
}
